/**
 * Check if a word looks like a plausible English word (contains at least one vowel).
 * Short words (1-2 chars) without vowels are likely OCR noise.
 */
const hasVowel = (word: string): boolean => /[aeiouAEIOU]/.test(word);

/**
 * Clean a single line of OCR text.
 * Returns empty string if the line is considered garbage.
 */
export const cleanOCRLine = (line: string): string => {
  let cleaned = line.trim();
  if (!cleaned) return '';

  // Collapse internal runs of whitespace to single space
  cleaned = cleaned.replace(/\s+/g, ' ');

  // Strip trailing punctuation that looks like OCR noise (lone dots, pipes, backslashes)
  cleaned = cleaned.replace(/[\s|\\.*]+$/, '').trim();
  if (!cleaned) return '';

  const alphaCount = (cleaned.match(/[a-zA-Z]/g) || []).length;
  const nonSpaceStr = cleaned.replace(/\s/g, '');
  const totalNonSpace = nonSpaceStr.length;

  if (totalNonSpace === 0) return '';

  // Single characters: only allow common standalone words
  if (totalNonSpace === 1) {
    return /^[AIa]$/.test(nonSpaceStr) ? cleaned : '';
  }

  // Short fragments (2-3 non-space chars): must be all alphanumeric AND contain a vowel
  // Filters: "J J", "NN", "kK" but keeps "OK", "Go", "an", "42"
  if (totalNonSpace <= 3) {
    const alphaNumCount = (cleaned.match(/[a-zA-Z0-9]/g) || []).length;
    if (alphaNumCount !== totalNonSpace) return '';
    // Pure numbers are fine (e.g. "42"), alpha fragments need a vowel
    if (alphaCount > 0 && !hasVowel(nonSpaceStr)) return '';
    return cleaned;
  }

  // Medium fragments (4-6 non-space chars): require alpha ratio >= 50% AND at least 3 alpha AND a vowel
  // Filters: ". kK 8", "NN oi" → wait "NN oi" has vowels... need word-level check
  if (totalNonSpace <= 6) {
    const alphaRatio = alphaCount / totalNonSpace;
    if (alphaRatio < 0.5) return '';
    if (alphaCount < 3) return '';
    // Check that at least one word (space-separated) is 3+ chars
    const words = cleaned.split(' ');
    const hasSubstantialWord = words.some((w) => w.length >= 3 && hasVowel(w));
    if (!hasSubstantialWord) return '';
    return cleaned;
  }

  // Longer fragments: require alpha ratio >= 40% AND at least 3 alpha characters
  const alphaRatio = alphaCount / totalNonSpace;
  if (alphaRatio < 0.4) return '';
  if (alphaCount < 3) return '';

  return cleaned;
};

/**
 * Clean the full OCR text output.
 * Applies per-line cleaning, removes garbage lines, collapses blank lines.
 */
export const cleanOCRText = (rawText: string): string => {
  const lines = rawText.split('\n');
  const cleanedLines = lines.map(cleanOCRLine);

  // Collapse consecutive empty lines (keep at most 1 blank line between paragraphs)
  const collapsed: string[] = [];
  let prevWasEmpty = true; // start true to skip leading blanks

  for (const line of cleanedLines) {
    if (line === '') {
      if (!prevWasEmpty) {
        collapsed.push('');
      }
      prevWasEmpty = true;
    } else {
      collapsed.push(line);
      prevWasEmpty = false;
    }
  }

  // Trim trailing empty lines
  while (collapsed.length > 0 && collapsed[collapsed.length - 1] === '') {
    collapsed.pop();
  }

  return collapsed.join('\n');
};

const MIN_CONFIDENCE = 30;
const MIN_TEXT_LENGTH = 2;

/**
 * Determine if a cleaned OCR result meets quality thresholds.
 * Returns true if the text should be displayed to the user.
 */
export const isQualityOCRResult = (cleanedText: string, confidence: number): boolean => {
  if (cleanedText.length < MIN_TEXT_LENGTH) return false;
  if (confidence < MIN_CONFIDENCE) return false;

  // Low confidence + short text = likely noise from a mostly-visual image
  if (confidence < 50 && cleanedText.length < 10) return false;

  return true;
};
